/**
 * @module AppInteraction
 * Handles bi-directional interaction between the NaaP App and the NaaP Kiosk.
 * This module provides the {@link AppInteraction} class which can be used to listen
 * for specific events from the NaaP Kiosk such as receiving context tokens or
 * application configuration.
 *
 * @example
 * const appInteraction = new AppInteraction();
 * appInteraction.on('config', (config) => initializeApp(config));
 *
 * @remarks
 * Must be paired with {@link KioskInteraction} in the NaaP Kiosk.
 */

import { EventEmitter } from 'eventemitter3';
import z from 'zod';
import { handleAck, waitForAck } from './ack.js';
import type { AppToKioskMessage, KioskToAppMessage } from './types.js';

type AppEvents = {
  /**
   * Emitted when receiving application configuration from NaaP Kiosk.
   * @param config Application configuration string.
   */
  config: (config: string) => void;
};

/**
 * Handles bi-directional interaction between the NaaP App and the NaaP Kiosk.
 *
 * @example
 * const appInteraction = new AppInteraction();
 * appInteraction.on('config', (config) => initializeApp(config));
 *
 * @remarks
 * Must be paired with {@link KioskInteraction} in the NaaP Kiosk.
 */
export class AppInteraction extends EventEmitter<AppEvents> {
  private messagePort?: MessagePort;
  private traceparent?: string;

  private static instance: AppInteraction | null = null;

  private constructor() {
    super();
  }

  /**
   * Returns the latest W3C traceparent received from the kiosk.
   * This is updated whenever a message containing a traceparent is received.
   * Use this to continue the distributed trace started by the kiosk/backend.
   * @returns The W3C traceparent string, or undefined if no trace context has been received.
   */
  public getTraceContext(): string | undefined {
    return this.traceparent;
  }

  /**
   * Initializes AppInteraction and waits for the MessageChannel to be ready.
   * @returns Promise resolving to a ready AppInteraction instance
   */
  public static async init(): Promise<AppInteraction> {
    // Make sure if the ui requests another app instance we do not create a new one
    // Otherwise we run into issues with the messagePort being reused which is not allowed
    // In case the page reloads the singleton is destroyed and a new one can be created
    if (AppInteraction.instance) return AppInteraction.instance;
    const localInstance = new AppInteraction();
    AppInteraction.instance = localInstance;

    return new Promise<AppInteraction>((resolve) => {
      function handlePort(event: MessageEvent) {
        if (event.data?.type === 'NAAP_INIT_CHANNEL') {
          if (event.data.traceparent) {
            localInstance.traceparent = event.data.traceparent;
          }
          if (event.ports?.[0]) {
            localInstance.messagePort = event.ports[0];
            localInstance.messagePort.onmessage = localInstance.handleMessage.bind(localInstance);
            localInstance.messagePort.start();
          } else {
            window.addEventListener('message', localInstance.handleMessage.bind(localInstance), false);
          }
          window.removeEventListener('message', handlePort);
          resolve(localInstance);
        }
      }
      window.addEventListener('message', handlePort, false);
      window.parent.postMessage({ type: 'APP_REQUEST_CHANNEL' }, '*');
    });
  }

  /**
   * Waits for an ack for the given ackId. Use this after sending a message that expects an ack.
   */
  private async waitForAck<T = unknown>(ackId: string, typeParser?: (result: unknown) => T): Promise<T> {
    const result = await waitForAck(ackId);
    return typeParser ? typeParser(result) : (result as T);
  }

  /**
   * Handles an incoming ack message. Call this when an ack arrives.
   */
  private handleAck(ackId: string, message: unknown, error: unknown): void {
    handleAck(ackId, message, error);
  }

  /**
   * @internal
   * Handles incoming message validation and event routing.
   */
  private handleMessage(event: MessageEvent) {
    const data = event.data as KioskToAppMessage | undefined;
    if (!data || typeof data !== 'object' || !('type' in data)) {
      return;
    }

    // Update trace context from any message that carries it
    if ('traceparent' in data && data.traceparent) {
      this.traceparent = data.traceparent;
    }

    switch (data.type) {
      case 'NAAP_CONFIG':
        this.emit('config', data.payload);
        break;
      case 'ACK_RESPONSE': {
        const ackMsg = data as Extract<KioskToAppMessage, { type: 'ACK_RESPONSE' }>;
        if (ackMsg.ackId) {
          this.handleAck(ackMsg.ackId, ackMsg.payload, ackMsg.error);
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * requests a new context token from the kiosk.
   * If the token is still valid it returns the existing one, otherwise a new one is created and returned
   */
  public requestContextToken(): Promise<string> {
    const ackId = crypto.randomUUID();

    const wait = this.waitForAck(ackId, (result) => z.string().parse(result));
    const msg: AppToKioskMessage = { type: 'APP_REQUEST_CONTEXT', ackId };
    this.sendMessage(msg);

    return wait;
  }

  /**
   * requests the current apps config from the kiosk
   */
  public requestConfig(): Promise<string> {
    const ackId = crypto.randomUUID();

    const wait = this.waitForAck(ackId, (result) => z.string().parse(result));
    const msg: AppToKioskMessage = { type: 'APP_REQUEST_CONFIG', ackId };
    this.sendMessage(msg);

    return wait;
  }

  private sendMessage(message: AppToKioskMessage) {
    if (this.messagePort) {
      this.messagePort.postMessage(message);
    } else {
      window.parent.postMessage(message, '*');
    }
  }
}
